/*
 * RoomThermostatRenderer — one room thermostat as an operator tile.
 *
 * Hero: room temperature. Under it: humidity + presence chip. A stepper
 * for the setpoint (writable when configured). When the window contact
 * reports open, the whole setpoint area greys out and a banner explains
 * why the controller isn't heating.
 */

import { useState } from 'preact/hooks';
import type { RoomThermostatWidget } from '../widgets';
import type { IPointBindingSource } from '../binding';
import {
  usePoint,
  useOptionalPoint,
  numeric,
  quality,
  clamp,
  coerceBool,
} from './_compound-helpers';
import { useLeafMeta } from '../LeafMetaContext';
import { formatLeafValue, formatNumber } from '../../format/leafValue';
import './RoomThermostatRenderer.css';

interface Props {
  widget: RoomThermostatWidget;
  source: IPointBindingSource;
}

export function RoomThermostatRenderer({ widget, source }: Props) {
  const room = usePoint(source, widget.roomTemperaturePoint);
  const setpoint = usePoint(source, widget.setpointPoint);
  const setpointMeta = useLeafMeta(widget.setpointPoint);
  const mode = useOptionalPoint(source, widget.modePoint);
  // Operating mode as the leaf's enum text ("3" -> "Komfort").
  const modeMeta = useLeafMeta(widget.modePoint);
  const humidity = useOptionalPoint(source, widget.humidityPoint);
  const windowContact = useOptionalPoint(source, widget.windowContactPoint);
  const presence = useOptionalPoint(source, widget.presencePoint);

  const roomVal = numeric(room.value.value?.value);
  const setpointVal = numeric(setpoint.value.value?.value);
  const humidityVal = numeric(humidity?.value.value?.value);
  const windowOpen = coerceBool(windowContact?.value.value?.value);
  const presenceBool = coerceBool(presence?.value.value?.value);

  const roomStale = quality(room.value.value) !== 'ok';
  const setpointStale = quality(setpoint.value.value) !== 'ok';
  const tileStale = roomStale && setpointStale;

  const min = widget.setpointMin ?? 10;
  const max = widget.setpointMax ?? 28;
  const step = widget.setpointStep ?? 0.5;

  // Read-only when the author marked it so or the leaf is not writable per
  // schema. Then there are no +/- buttons at all; disabled ones look like a
  // fault rather than a display.
  const readOnly = widget.setpointWritable === false || setpointMeta?.writable === false;
  const setpointWritable = !readOnly && windowOpen !== true;

  const [pendingSetpoint, setPendingSetpoint] = useState<number | null>(null);
  const [pendingState, setPendingState] = useState<'sending' | 'rejected' | null>(null);

  async function commitSetpoint(target: number) {
    if (!setpointWritable) return;
    setPendingSetpoint(target);
    setPendingState('sending');
    const ac = new AbortController();
    try {
      for await (const ack of source.write(widget.setpointPoint, target, ac.signal)) {
        if (ack.state === 'confirmed') {
          setPendingSetpoint(null);
          setPendingState(null);
          return;
        }
        if (ack.state === 'rejected') {
          setPendingState('rejected');
          return;
        }
      }
      setPendingSetpoint(null);
      setPendingState(null);
    } catch {
      setPendingSetpoint(null);
      setPendingState(null);
    }
  }

  const shownSetpoint = pendingSetpoint ?? setpointVal;

  return (
    <div class={`hv-room-thermo${tileStale ? ' hv-room-thermo--stale' : ''}`}>
      <header class="hv-room-thermo__head">
        <div class="hv-room-thermo__title">
          <div class="hv-room-thermo__name">{widget.roomName}</div>
          {widget.label && <div class="hv-room-thermo__label">{widget.label}</div>}
        </div>
        {mode && <ModeChip value={formatLeafValue(mode.value.value?.value, modeMeta)} />}
      </header>

      <div class="hv-room-thermo__body">
        <div class="hv-room-thermo__hero">
          <div class="hv-room-thermo__hero-value">
            {roomVal != null ? formatNumber(roomVal, 1) : '—'}<span>°C</span>
          </div>
          <div class="hv-room-thermo__subline">
            {humidityVal != null && (
              <span class="hv-room-thermo__humidity">{Math.round(humidityVal)} %</span>
            )}
            {presence && (
              <span class={`hv-room-thermo__presence hv-room-thermo__presence--${presenceBool === true ? 'in' : presenceBool === false ? 'out' : 'unknown'}`}>
                {presenceBool === true ? 'belegt' : presenceBool === false ? 'leer' : '—'}
              </span>
            )}
          </div>
        </div>

        <div class={`hv-room-thermo__setpoint${readOnly ? ' hv-room-thermo__setpoint--readonly' : !setpointWritable ? ' hv-room-thermo__setpoint--disabled' : ''}`}>
          {!readOnly && (
            <button
              type="button"
              class="hv-room-thermo__step"
              onClick={() => shownSetpoint != null && commitSetpoint(clamp(shownSetpoint - step, min, max))}
              disabled={!setpointWritable || shownSetpoint == null || pendingState === 'sending'}
              aria-label="Sollwert verringern"
            >−</button>
          )}
          <div class="hv-room-thermo__setpoint-value">
            {/* Without +/- nothing else tells the reader that this number is
                the setpoint and not a second reading. */}
            {readOnly && <span class="hv-room-thermo__setpoint-caption">Soll</span>}
            <span class="hv-room-thermo__setpoint-num">
              {shownSetpoint != null ? formatNumber(shownSetpoint, 1) : '—'}
            </span>
            <span class="hv-room-thermo__setpoint-unit">°C</span>
            {pendingState === 'sending' && <span class="hv-room-thermo__pending">…</span>}
            {pendingState === 'rejected' && <span class="hv-room-thermo__rejected">✕</span>}
          </div>
          {!readOnly && (
            <button
              type="button"
              class="hv-room-thermo__step"
              onClick={() => shownSetpoint != null && commitSetpoint(clamp(shownSetpoint + step, min, max))}
              disabled={!setpointWritable || shownSetpoint == null || pendingState === 'sending'}
              aria-label="Sollwert erhöhen"
            >+</button>
          )}
        </div>
      </div>

      {windowOpen === true && (
        <div class="hv-room-thermo__window" role="alert">
          Fenster offen, Regelung gestoppt
        </div>
      )}
    </div>
  );
}

// ---------- Mode chip ---------------------------------------------------

function ModeChip({ value }: { value: unknown }) {
  const raw = String(value ?? '').trim();
  const s = raw.toLowerCase();
  let tone: 'comfort' | 'standby' | 'absent' | 'frost' | 'unknown' = 'unknown';
  let label = raw || '—';
  if (s.includes('komfort') || s.includes('comfort')) { tone = 'comfort'; label = raw || 'Komfort'; }
  else if (s.includes('standby') || s.includes('bereit')) { tone = 'standby'; label = raw || 'Standby'; }
  else if (s.includes('abwesend') || s.includes('absent') || s.includes('away')) { tone = 'absent'; label = raw || 'Abwesend'; }
  else if (s.includes('frost')) { tone = 'frost'; label = raw || 'Frostschutz'; }
  return <span class={`hv-room-thermo__mode hv-room-thermo__mode--${tone}`}>{label}</span>;
}
